import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Star, Gift, Car, CircleDot, Wrench, Sparkles, MessageCircle, Mail } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useFlags } from "@/hooks/useFlags";
import { Button } from "@/components/ui/button";

const CAT_ICON = { concessionnaire: Car, pneus: CircleDot, entretien: Wrench };

export function RewardsPanel() {
  const { t } = useTranslation();
  const v2 = useFlags().v2_enabled;
  const [rewards, setRewards] = useState([]);
  const [points, setPoints] = useState(0);
  const [redemptions, setRedemptions] = useState([]);
  const [referral, setReferral] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/rewards").then((r) => setRewards(r.data.rewards)).catch(() => {});
    api.get("/me/points").then((r) => setPoints(r.data.points)).catch(() => {});
    api.get("/me/redemptions").then((r) => setRedemptions(r.data.redemptions)).catch(() => {});
    api.get("/me/referral").then((r) => setReferral(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const redeem = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/rewards/${id}/redeem`);
      toast.success(`${t("rewards.redeemed")} — ${data.code}`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setBusy("");
  };

  return (
    <div data-testid="rewards-panel" className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 items-stretch">
        <div className="flex-1 bg-gradient-to-br from-canard to-emeraude-dark text-white rounded-2xl p-6">
          <p className="text-xs uppercase font-bold text-white/80 tracking-wide flex items-center gap-1"><Star className="w-3.5 h-3.5" /> {t("plans.points")}</p>
          <p data-testid="rewards-points" className="font-heading font-extrabold text-4xl mt-1">{points}</p>
          <p className="text-[11px] text-white/70 mt-2">{t("rewards.info")}</p>
        </div>
        {redemptions.length > 0 && (
          <div className="flex-1 bg-white rounded-2xl border border-border p-5">
            <p className="font-heading font-bold text-ink flex items-center gap-2 mb-3"><Gift className="w-4 h-4 text-canard" /> {t("rewards.myRewards")}</p>
            <ul className="space-y-2 max-h-32 overflow-auto">
              {redemptions.map((r) => (
                <li key={r.id} data-testid={`redemption-${r.id}`} className="text-sm flex justify-between gap-2">
                  <span className="text-ink truncate">{r.reward_title}</span>
                  <span className="font-mono font-bold text-canard-dark shrink-0">{r.code}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {referral && (
        <div data-testid="referral-card" className="bg-white rounded-2xl border border-border p-5">
          <p className="font-heading font-bold text-ink flex items-center gap-2"><Gift className="w-4 h-4 text-canard" /> {t("referral.title")}</p>
          <p className="text-sm text-muted2 font-body mt-1">{t("referral.desc", { signup: referral.signup_bonus, booking: referral.booking_bonus })}</p>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <code data-testid="referral-code" className="bg-sand rounded-full px-4 py-2 font-mono font-extrabold text-canard-dark tracking-wider">{referral.code}</code>
            <Button data-testid="referral-copy" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/register?ref=${referral.code}`); toast.success(t("referral.copied")); }} className="rounded-full bg-canard hover:bg-canard-dark font-bold h-9">{t("referral.copy")}</Button>
            <a data-testid="referral-whatsapp" href={`https://wa.me/?text=${encodeURIComponent(t("referral.shareMsg") + " " + window.location.origin + "/register?ref=" + referral.code)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-[#25D366] text-white font-bold h-9 px-4 text-sm hover:opacity-90"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
            <a data-testid="referral-email" href={`mailto:?subject=${encodeURIComponent(t("referral.title"))}&body=${encodeURIComponent(t("referral.shareMsg") + " " + window.location.origin + "/register?ref=" + referral.code)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-sand text-ink font-bold h-9 px-4 text-sm hover:bg-pastel"><Mail className="w-4 h-4" /> Email</a>
          </div>
          <div className="flex gap-6 mt-4">
            <div><p className="font-heading font-extrabold text-2xl text-canard" data-testid="referral-count">{referral.referrals_count}</p><p className="text-xs text-muted2">{t("referral.count")}</p></div>
            <div><p className="font-heading font-extrabold text-2xl text-emeraude-dark" data-testid="referral-earned">{referral.referrals_count * referral.signup_bonus}</p><p className="text-xs text-muted2">{t("referral.earned")}</p></div>
          </div>
          <p className="text-xs font-bold text-muted2 uppercase tracking-wide mt-5">{t("referralList.title")}</p>
          {referral.referrals?.length > 0 ? (
            <ul className="mt-2 space-y-2" data-testid="referral-list">
              {referral.referrals.map((f, i) => (
                <li key={i} data-testid={`referral-item-${i}`} className="flex items-center justify-between gap-2 bg-sand rounded-xl px-3 py-2">
                  <span className="text-sm font-semibold text-ink truncate">{f.name}</span>
                  {f.booked ? (
                    <span className="text-xs font-bold text-emeraude-dark bg-emeraude/15 rounded-full px-2 py-0.5 shrink-0" data-testid={`referral-status-${i}`}>{t("referralList.booked", { pts: referral.booking_bonus })}</span>
                  ) : (
                    <span className="text-xs font-bold text-yellow-700 bg-yellow-100 rounded-full px-2 py-0.5 shrink-0" data-testid={`referral-status-${i}`}>{t("referralList.pending")}</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-muted2 font-body" data-testid="referral-empty">{t("referralList.empty")}</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {v2 && rewards.map((r) => {
          const Icon = CAT_ICON[r.category] || Sparkles;
          const affordable = points >= r.points_cost;
          return (
            <div key={r.id} data-testid={`reward-${r.id}`} className="bg-white rounded-2xl border border-border p-5 flex gap-4">
              <div className="w-12 h-12 rounded-xl bg-canard/10 flex items-center justify-center text-canard shrink-0"><Icon className="w-6 h-6" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase font-bold text-muted2 tracking-wide">{r.partner}</p>
                <p className="font-heading font-bold text-ink">{r.title}</p>
                <p className="text-sm text-muted2 font-body mt-1">{r.description}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="font-heading font-extrabold text-canard flex items-center gap-1"><Star className="w-4 h-4" /> {r.points_cost}</span>
                  <Button data-testid={`redeem-${r.id}`} disabled={!affordable || busy === r.id} onClick={() => redeem(r.id)} className="rounded-full bg-canard hover:bg-canard-dark font-bold h-8 disabled:opacity-40">
                    {busy === r.id ? "..." : t("rewards.redeem")}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
